/**
 * Runtime validation of everything the backend sends (§10, §44).
 *
 * §44 is the reason this file exists rather than trusting the generated
 * types: "Validate at runtime", "Handle optional/null fields", "Allow unknown
 * future fields", "If schema invalid, show safe fallback and log telemetry."
 *
 * Generated types describe what the backend *should* send. These schemas
 * check what it *did* send, which matters most for AI output — the one part
 * of the payload a model had a hand in shaping.
 *
 * Every object is permissive about extra keys (`.passthrough()` semantics are
 * Zod's default for `.object()` when parsing, and we never `.strict()`), so a
 * backend that adds a field tomorrow does not break a child's screen today.
 */

import { z } from 'zod';

export const languageSchema = z.enum(['en', 'hinglish']);

export const masteryStateSchema = z.enum([
  'not_learned',
  'learning',
  'practicing',
  'mastered',
  'retention_review',
]);

// --- auth -------------------------------------------------------------------
export const sessionTokensSchema = z.object({
  access_token: z.string().min(1),
  refresh_token: z.string().min(1),
  token_type: z.string().default('Bearer'),
  expires_in: z.number().int().positive(),
});

export const authenticatedUserSchema = z.object({
  user_id: z.string(),
  role: z.enum(['student', 'parent', 'admin', 'service']),
  profile_complete: z.boolean(),
});

export const exchangeResponseSchema = z.object({
  tokens: sessionTokensSchema,
  user: authenticatedUserSchema,
});

export const magicLinkResponseSchema = z.object({
  sent: z.boolean(),
  expires_in_seconds: z.number().int(),
  // Present only outside production, so local and CI flows need no WhatsApp.
  debug_token: z.string().nullish(),
});

// --- student ----------------------------------------------------------------
export const studentProfileSchema = z.object({
  user_id: z.string(),
  display_name: z.string().nullish(),
  class_level: z.number().int(),
  board_code: z.string(),
  board_name: z.string(),
  preferred_language: languageSchema.catch('en'),
  timezone: z.string(),
});

export const skillMasterySchema = z.object({
  skill_id: z.string(),
  skill_name: z.string(),
  topic_name: z.string(),
  mastery_score: z.number(),
  state: masteryStateSchema.catch('not_learned'),
  confidence: z.number(),
  trend: z.number(),
  attempts: z.number().int(),
  last_attempt_at: z.string().nullish(),
});

export const masterySummarySchema = z.object({
  student_id: z.string(),
  skills_tracked: z.number().int(),
  skills_mastered: z.number().int(),
  skills_needing_attention: z.number().int(),
  average_mastery: z.number(),
  skills: z.array(skillMasterySchema),
});

export const gamificationSchema = z.object({
  xp_total: z.number().int(),
  level: z.number().int(),
  current_streak: z.number().int(),
  longest_streak: z.number().int(),
  last_activity_at: z.string().nullish(),
  badges: z.array(z.object({ code: z.string(), name: z.string(), awarded_at: z.string() })),
});

export const recommendationSchema = z.object({
  recommendation_id: z.string(),
  action_type: z.string(),
  skill_id: z.string().nullish(),
  skill_name: z.string().nullish(),
  // §46: "Child-friendly reason. No algorithm/rule names." The reason object
  // carries `rule` for analytics; the UI reads `explanation` and nothing else.
  reason: z.record(z.unknown()).default({}),
  rule_version: z.string(),
  status: z.string(),
  created_at: z.string(),
});

export const activityItemSchema = z.object({
  kind: z.string(),
  occurred_at: z.string(),
  skill_id: z.string().nullish(),
  skill_name: z.string().nullish(),
  detail: z.string().nullish(),
});

export const skillProgressDetailSchema = z.object({
  skill_id: z.string(),
  skill_name: z.string(),
  topic_name: z.string(),
  mastery_score: z.number(),
  state: masteryStateSchema.catch('not_learned'),
  confidence: z.number(),
  trend: z.number(),
  attempts: z.number().int(),
  correct_count: z.number().int(),
  last_attempt_at: z.string().nullish(),
  active_misconceptions: z.array(z.record(z.unknown())).default([]),
  recent_changes: z.array(z.record(z.unknown())).default([]),
});

// --- curriculum -------------------------------------------------------------
export const skillSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  difficulty: z.number(),
  order_index: z.number().int(),
});

export const topicSchema = z.object({
  id: z.string(),
  name: z.string(),
  order_index: z.number().int(),
  skills: z.array(skillSchema),
});

export const chapterSchema = z.object({
  id: z.string(),
  name: z.string(),
  order_index: z.number().int(),
  subject_id: z.string(),
  subject_name: z.string(),
  topics: z.array(topicSchema),
});

export const curriculumSchema = z.object({
  curriculum_version_id: z.string(),
  board_code: z.string(),
  class_level: z.number().int(),
  version: z.string(),
  chapters: z.array(chapterSchema),
});

export const skillDetailSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  difficulty: z.number(),
  topic_id: z.string(),
  topic_name: z.string(),
  chapter_id: z.string(),
  chapter_name: z.string(),
  subject_id: z.string(),
  subject_name: z.string(),
});

// --- homework ---------------------------------------------------------------
export const homeworkSessionSchema = z.object({
  session_id: z.string(),
  status: z.string(),
});

export const presignSchema = z.object({
  asset_id: z.string(),
  upload_url: z.string(),
  expires_at: z.string(),
  required_headers: z.record(z.string()).default({}),
});

export const submissionSchema = z.object({
  submission_id: z.string(),
  status: z.string(),
  skill_id: z.string().nullish(),
  confidence: z.number().nullish(),
  needs_clarification: z.boolean().default(false),
  failure_reason: z.string().nullish(),
  created_at: z.string().nullish(),
});

/**
 * The §11 TutorResponse, as rendered.
 *
 * `steps` is the only field the UI truly requires — §44 says to "render only
 * supported response types" and fall back safely otherwise. Everything else
 * is optional so a partial-but-valid response still teaches the child
 * something instead of showing an error.
 */
/**
 * The verdict on an answer, carried on the turn the child said it.
 *
 * Its own schema because it is read in two places: inside a tutor response,
 * and straight off a doubt's untyped `content` record in the thread, which
 * only needs this field and should not have to parse the whole response to
 * get it.
 */
export const answerAttemptSchema = z.object({
  answer: z.string(),
  is_correct: z.boolean(),
});

export const tutorContentSchema = z.object({
  response_type: z.string(),
  language: languageSchema.catch('en'),
  title: z.string().default(''),
  steps: z
    .array(z.object({ step: z.number().int(), text: z.string() }))
    .default([]),
  hint: z.string().nullish(),
  check_question: z.string().nullish(),
  /**
   * The reply written to be heard rather than read — the method in a breath,
   * then the ask. Absent when there is nothing worth saying aloud, and the
   * play button simply does not appear.
   */
  spoken_summary: z.string().nullish(),
  /**
   * Where the child's own working first goes wrong, when the backend read it.
   *
   * Absent for a typed question, for a blank page, and — the case worth
   * naming — for handwriting the OCR could not resolve. The backend does not
   * show the model working it could not read, so silence here means "we did
   * not look", never "your working is fine".
   */
  attempt_diagnosis: z.string().nullish(),
  /**
   * The formula this question runs on, when it has one.
   *
   * Its own field rather than a sentence inside step 2. A child three
   * messages into an explanation of equivalent fractions asked "do we have
   * any formula?" — the rule had been in the steps the whole time, written
   * as prose, so it was never findable as a rule.
   *
   * Null for a question with no formula. "Which of these are equivalent?" is
   * a comparison, and showing a rule there would teach a child to reach for
   * algebra where looking would do.
   */
  rule: z
    .object({
      name: z.string(),
      /** LaTeX, so it renders as a real stacked fraction rather than `a/b`. */
      formula: z.string(),
      means: z.string(),
      /** The rule used once, on different numbers from this question. */
      example: z.string().nullish(),
    })
    .nullish(),
  /*
   * The last step, which is the child's to work out.
   *
   * Before they answer, the backend sends `{ prompt }` and nothing else --
   * `value` and `worked_step` are stripped server-side, not hidden here. That
   * matters: a client-side hide is one devtools panel away from being no gate
   * at all, and children who can open devtools are exactly the ones who will.
   *
   * Absent entirely when there is nothing to withhold: a clarifying question,
   * or a question with no single checkable answer. The answer box keys off
   * `answer?.prompt`, so absent means no box rather than an empty one.
   */
  answer: z
    .object({
      prompt: z.string(),
      value: z.string().nullish(),
      worked_step: z.string().nullish(),
    })
    .nullish(),
  /**
   * The verdict on an answer the child typed into the doubt box.
   *
   * Two inputs sit on the homework screen and both look like somewhere to
   * put an answer. A child asked "1/3 of 12 apples" typed hers into this
   * one, got it right, and was re-taught instead of told — so the backend
   * now grades a stated answer the same way it grades the answer field, and
   * sends the verdict back on the turn it was said.
   *
   * Absent on every turn that was not an answer, which is most of them.
   * `is_correct` is never null here: the backend only records an attempt it
   * could actually mark.
   */
  answer_attempt: answerAttemptSchema.nullish(),
  practice_recommended: z.boolean().default(false),
  recall_prompt: z.string().nullish(),
  uncertain: z.boolean().default(false),
  // Deliberately not typed further: memory_candidates are backend internals
  // and §47 says memory is never a child-facing database.
  memory_candidates: z.array(z.unknown()).default([]),
});

/** The clarifying-question shape the backend sends instead of an explanation. */
export const clarifyContentSchema = z.object({
  response_type: z.literal('clarifying_question'),
  language: languageSchema.catch('en'),
  question: z.string(),
  reason: z.string().nullish(),
});

/** One question read off a photographed page (§13.5b). */
export const detectedQuestionSchema = z.object({
  id: z.string(),
  /** The printed number: "1", "2". */
  number_label: z.string(),
  /** The printed letter, when the question has parts. Null when it stands alone. */
  part_label: z.string().nullish(),
  /** The instruction the parts share — shown once, as a heading. */
  stem_text: z.string().nullish(),
  /** The question itself, as OCR read it off the page. */
  text: z.string(),
});

export type DetectedQuestion = z.infer<typeof detectedQuestionSchema>;

/**
 * The backend asking which question, instead of answering one.
 *
 * Sent when a photo turns out to hold a whole exercise. Carries no prompt
 * text: the heading is interface copy and belongs with the rest of the app's
 * own strings, so it reads in the child's language without a round trip.
 */
export const questionPickerContentSchema = z.object({
  response_type: z.literal('question_picker'),
  language: languageSchema.catch('en'),
  questions: z.array(detectedQuestionSchema).min(1),
});

export const doubtSchema = z.object({
  doubt_id: z.string(),
  turn: z.number().int(),
  /** What the child said, or what OCR read off their photo. */
  doubt_text: z.string(),
  input_type: z.string().default('text'),
  /** Short-lived signed URL for a photo they sent, so the thread shows it. */
  image_url: z.string().nullish(),
  schema_version: z.string(),
  content: z.record(z.unknown()),
  created_at: z.string().nullish(),
});

export const homeworkResponseSchema = z.object({
  response_id: z.string(),
  submission_id: z.string(),
  response_type: z.string(),
  language: languageSchema.nullish(),
  schema_version: z.string(),
  content: z.record(z.unknown()),
  audio_url: z.string().nullish(),
  answer_revealed: z.boolean().default(false),
  doubts: z.array(doubtSchema).default([]),
  created_at: z.string(),
});

/**
 * A reply to a child stuck mid-question.
 *
 * No `answer` field, deliberately, and not only here: the backend never loads
 * the question's answer key on this path, so there is nothing for one to
 * arrive in.
 */
export const practiceHelpSchema = z.object({
  help_id: z.string(),
  schema_version: z.string(),
  content: z.object({
    response_type: z.string(),
    language: languageSchema.catch('en'),
    steps: z.array(z.object({ step: z.number().int(), text: z.string() })).default([]),
    /** Null means we could not read their working — never that it is fine. */
    spotted: z.string().nullish(),
    next_step: z.string().nullish(),
    spoken_summary: z.string().nullish(),
  }),
});

export const planSchema = z.object({
  code: z.string(),
  name: z.string(),
  price_minor: z.number().int(),
  currency: z.string(),
  interval: z.string(),
  interval_count: z.number().int(),
  child_limit: z.number().int(),
});

/**
 * One tier of prices, and the family they were chosen for.
 *
 * Not the whole price list — the server picks the tier from how many
 * children are on the account, so a parent of one never reads the two-child
 * numbers. `covers` is lower than `children` only when the family is bigger
 * than any plan we sell, which the page says out loud rather than quietly
 * under-selling them.
 */
export const plansSchema = z.object({
  plans: z.array(planSchema),
  children: z.number().int(),
  covers: z.number().int(),
});

/**
 * What the banner reads. No price, no plan, no payer.
 *
 * A child's session can call the endpoint behind this, so it answers "is the
 * product about to stop, and when" and nothing about what their family pays.
 */
export const subscriptionStatusSchema = z.object({
  status: z.string().nullish(),
  allowed: z.boolean(),
  ends_at: z.string().nullish(),
  days_left: z.number().int().nullish(),
  should_prompt: z.boolean(),
});

export const checkoutSchema = z.object({
  subscription_id: z.string(),
  /** The publishable half of the Razorpay pair; it authorises nothing. */
  key_id: z.string().nullish(),
  plan_code: z.string(),
  amount_minor: z.number().int(),
  currency: z.string(),
  /** Razorpay's own hosted page — the fallback for a browser where the
   *  checkout script will not load, which on a cheap Android is not rare. */
  short_url: z.string().nullish(),
});

export const narrationSchema = z.object({
  /** Null when there was nothing to say or the provider was down (§6A.3). */
  audio_url: z.string().nullish(),
});

/**
 * Why the gate was opened without an answer.
 *
 * Two values, not one, because they call for opposite responses from us: a
 * child who gives up every time needs easier questions, and a child who
 * skips every time needs a different conversation.
 */
export type RevealReason = 'gave_up' | 'skipped';

/**
 * One exchange in a conversation about a syllabus skill (§22).
 *
 * The reply reuses `tutorContentSchema`: it is the same shape minus the parts
 * that only exist to withhold an answer, and nothing here withholds one. The
 * two fields it adds are the two decisions the backend made about the
 * question — whether it was really homework, and whether it was really about
 * this skill.
 */
export const topicTurnSchema = z.object({
  turn_id: z.string(),
  turn: z.number().int(),
  asked: z.string(),
  schema_version: z.string().nullish(),
  content: z.record(z.string(), z.unknown()).default({}),
  about_this_skill: z.boolean().default(true),
  created_at: z.string().nullish(),
});

export const topicChatSchema = z.object({
  skill_id: z.string(),
  turns: z.array(topicTurnSchema).default([]),
});

export const answerResultSchema = z.object({
  /** null when we could not mark it -- they still get the working. */
  is_correct: z.boolean().nullish(),
  value: z.string(),
  worked_step: z.string(),
});

// --- practice ---------------------------------------------------------------
export const practiceQuestionSchema = z.object({
  question_id: z.string(),
  question: z.string(),
  hint: z.string().nullish(),
  answer_schema: z.record(z.unknown()).default({}),
  // §45: "Do not expose raw difficulty." Parsed so the client can pick an
  // input, never rendered.
  difficulty: z.number(),
  language: languageSchema.nullish(),
  order_index: z.number().int(),
  answered: z.boolean().default(false),
  was_correct: z.boolean().nullish(),
});

export const practiceSessionSchema = z.object({
  session_id: z.string(),
  skill_id: z.string(),
  status: z.string(),
  source: z.string(),
  language: languageSchema.nullish(),
  question_count: z.number().int(),
  correct_count: z.number().int(),
  questions: z.array(practiceQuestionSchema),
  created_at: z.string(),
});

export const attemptResultSchema = z.object({
  attempt_id: z.string(),
  is_correct: z.boolean(),
  score: z.number(),
  feedback: z.string(),
  explanation: z.string().nullish(),
  graded_by: z.string(),
  mastery_score: z.number(),
  mastery_state: masteryStateSchema.catch('not_learned'),
  mastery_delta: z.number(),
  xp_awarded: z.number().int(),
  current_streak: z.number().int(),
  session_completed: z.boolean(),
  replayed: z.boolean().default(false),
});

// --- parent -----------------------------------------------------------------
export const childSummarySchema = z.object({
  student_id: z.string(),
  display_name: z.string().nullish(),
  class_level: z.number().int(),
  board_code: z.string(),
});

/**
 * The state of a child's standing sign-in link.
 *
 * No token field, and not by omission: the backend stores only a hash, so
 * there is nothing to send back. Re-sending issues a new link, which also
 * kills the old one -- the way out of a link having gone astray.
 */
export const childLinkSchema = z.object({
  sent: z.boolean(),
  expires_at: z.string().nullish(),
  redemption_count: z.number().int().default(0),
  last_used_at: z.string().nullish(),
});

export const skillProgressSchema = z.object({
  skill_name: z.string(),
  topic_name: z.string(),
  subject_name: z.string(),
  mastery_score: z.number(),
  direction: z.string(),
});

export const parentProgressSchema = z.object({
  child: childSummarySchema,
  topics_practiced: z.array(z.string()).default([]),
  skills_improving: z.array(skillProgressSchema).default([]),
  skills_needing_attention: z.array(skillProgressSchema).default([]),
  homework_count: z.number().int().default(0),
  practice_count: z.number().int().default(0),
  current_streak: z.number().int().default(0),
  last_activity_at: z.string().nullish(),
  activity_trend: z
    .array(
      z.object({
        day: z.string(),
        homework_count: z.number().int(),
        practice_count: z.number().int(),
      }),
    )
    .default([]),
  recurring_learning_areas: z.array(z.string()).default([]),
});

// --- parent onboarding ------------------------------------------------------
/**
 * A child, plus whether this device can be handed to them.
 *
 * False for a child who signs in with their own number — the accounts that
 * predate parent-first onboarding. The backend refuses a hand-off for those,
 * so the list must know before it draws the button.
 *
 * Defaults to true rather than false: if the field ever goes missing, the
 * button appears and the backend explains why it cannot be used. §42 —
 * frontend authorization is an affordance, and the backend is what says no.
 */
export const managedChildSummarySchema = childSummarySchema.extend({
  can_hand_off: z.boolean().catch(true),
});

export const parentOverviewSchema = z.object({
  user_id: z.string(),
  display_name: z.string().nullish(),
  preferred_language: languageSchema.catch('en'),
  timezone: z.string().default('Asia/Kolkata'),
  // Whether a PIN exists. The PIN itself never leaves the parent's fingers.
  has_pin: z.boolean(),
  children: z.array(managedChildSummarySchema).default([]),
});

/** The picker: everyone on this account. */
export const profileChoicesSchema = z.object({
  parent_name: z.string().nullish(),
  /**
   * Whether "Continue as parent" can ask for a PIN, or has to send them back
   * to their sign-in link. Never the PIN, never its hash, never its length.
   */
  parent_has_pin: z.boolean(),
  current_user_id: z.string(),
  children: z.array(childSummarySchema).default([]),
});

/**
 * The device has changed hands.
 *
 * The caller's session is revoked server-side as part of this, so these
 * tokens are the only live credential afterwards. `child` is present for a
 * child profile and null for the parent, which is what tells the client
 * where to go next.
 */
export const enterProfileSchema = z.object({
  tokens: sessionTokensSchema,
  user: authenticatedUserSchema,
  child: childSummarySchema.nullish(),
});

// --- inferred types ---------------------------------------------------------
export type StudentProfile = z.infer<typeof studentProfileSchema>;
export type MasterySummary = z.infer<typeof masterySummarySchema>;
export type SkillMastery = z.infer<typeof skillMasterySchema>;
export type Gamification = z.infer<typeof gamificationSchema>;
export type Recommendation = z.infer<typeof recommendationSchema>;
export type ActivityItem = z.infer<typeof activityItemSchema>;
export type SkillProgressDetail = z.infer<typeof skillProgressDetailSchema>;
export type Curriculum = z.infer<typeof curriculumSchema>;
export type Chapter = z.infer<typeof chapterSchema>;
export type Topic = z.infer<typeof topicSchema>;
export type Skill = z.infer<typeof skillSchema>;
export type SkillDetail = z.infer<typeof skillDetailSchema>;
export type HomeworkSubmission = z.infer<typeof submissionSchema>;
export type HomeworkResponse = z.infer<typeof homeworkResponseSchema>;
export type ProfileChoices = z.infer<typeof profileChoicesSchema>;
export type TutorContent = z.infer<typeof tutorContentSchema>;
export type Doubt = z.infer<typeof doubtSchema>;
export type AnswerResult = z.infer<typeof answerResultSchema>;
export type TopicTurn = z.infer<typeof topicTurnSchema>;
export type TopicChat = z.infer<typeof topicChatSchema>;
export type ClarifyContent = z.infer<typeof clarifyContentSchema>;
export type PracticeSession = z.infer<typeof practiceSessionSchema>;
export type PracticeQuestion = z.infer<typeof practiceQuestionSchema>;
export type AttemptResult = z.infer<typeof attemptResultSchema>;
export type ParentProgress = z.infer<typeof parentProgressSchema>;
export type SkillProgress = z.infer<typeof skillProgressSchema>;
export type ChildSummary = z.infer<typeof childSummarySchema>;
export type ChildLink = z.infer<typeof childLinkSchema>;
export type ParentOverview = z.infer<typeof parentOverviewSchema>;
export type ManagedChildSummary = z.infer<typeof managedChildSummarySchema>;
export type EnterProfile = z.infer<typeof enterProfileSchema>;
export type SessionTokens = z.infer<typeof sessionTokensSchema>;
export type ExchangeResponse = z.infer<typeof exchangeResponseSchema>;
